import React, { useState, useRef, useEffect } from 'react';
import './NSCarousel.scss';

export interface INSCarouselProps {
    children: React.ReactNode;
    id: string;
    className?: string;
}

const NSCarousel: React.FC<INSCarouselProps> = ({
    children,
    id,
    className = '',
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    const childrenArray = React.Children.toArray(children);
    const totalItems = childrenArray.length;

    useEffect(() => {
        const { current: container } = containerRef;
        if (!container) return undefined;

        const handleScroll = () => {
            const { scrollLeft, scrollWidth, offsetWidth } = container;

            // Use 5px tolerance for subpixel rendering and zoom handling
            if (scrollLeft + offsetWidth >= scrollWidth - 5) {
                setCurrentIndex(totalItems - 1);
            } else {
                const index = Math.round(scrollLeft / offsetWidth);
                setCurrentIndex(Math.min(index, totalItems - 1));
            }
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, [totalItems]);

    const handleDotClick = (index: number) => {
        const { current: container } = containerRef;
        if (!container) return;

        // Items are 85% width, so calculate scroll position based on that
        // scroll-snap will handle the final alignment to the item start
        const { offsetWidth } = container;
        const itemWidth = offsetWidth * 0.85;
        container.scrollTo({
            left: index * itemWidth,
            behavior: 'smooth',
        });
    };

    return (
        <div className={`NSCarousel ${className}`} role="region" aria-label="Content carousel">
            {/* eslint-disable react/no-array-index-key */}
            <div className="NSCarousel__Container" ref={containerRef} aria-live="polite">
                {childrenArray.map((child, index) => (
                    <div key={`${id}-carousel-item-${index}`} className="NSCarousel__Item">
                        {child}
                    </div>
                ))}
            </div>
            {totalItems > 1 && (
                <div className="NSCarousel__Dots" role="group" aria-label="Carousel navigation">
                    {childrenArray.map((_, index) => (
                        <button
                            key={`${id}-carousel-dot-${index}`}
                            type="button"
                            className={`NSCarousel__Dot ${index === currentIndex ? 'NSCarousel__Dot--active' : ''}`}
                            aria-label={`Go to slide ${index + 1} of ${totalItems}`}
                            aria-current={index === currentIndex ? 'true' : undefined}
                            onClick={() => handleDotClick(index)}
                        />
                    ))}
                </div>
            )}
            {/* eslint-enable react/no-array-index-key */}
        </div>
    );
};

export default NSCarousel;
